import { GOAL, freqToMidi, isValidVoice } from '@muzly/voice-engine';
import type { Clip } from '@muzly/voice-engine';

import { rangeHz } from './capture';
import { melodySpanSec } from './melody';
import { noteParams } from '../../core';

import type { SegmentId } from './capture';
import type { MelodyNote } from './melody';
import type { CopyRef } from '../../core';

/**
 * recordTests — 진단 녹음의 순수 설정.
 *
 * **진단은 4단계다**: 아~ 길게 / 저음역대 / 고음역대 / 노래 따라부르기. 시안이 모든
 * 측정 화면에 `n/4`를 찍고, 저음과 고음은 재는 것도 그림도 다르므로 각각이 하나의 진단이다
 * (한 클립에서 양끝 퍼센타일을 뽑으면 오르내리는 중간 음이 극값을 오염시킨다 — 그래서
 * 애초에 녹음이 갈라져 있었다).
 *
 * 진단 하나가 세그먼트 하나를 갖는다. `segments`가 배열인 모양은 그대로 두었다: 캡처·집계
 * (`framesToClips`)와 재개 저장(`mergeCaptured`)이 세그먼트 단위로 돌고, 나중에 한 진단이
 * 다시 여러 녹음을 묶을 여지를 남긴다.
 *
 * 화면 문구는 전부 i18n 키다 — 여기는 키만 들고 있고 번역은 `libs/shared/src/i18n`에 있다.
 * React·시간 의존 없음 — vitest 대상.
 */

/**
 * 진단(테스트) 식별자. 화면(안내 팝업 목록·가이드 카드 `n/N`)은 전부 `RECORD_TESTS`에서
 * 세므로, 진단이 늘고 주는 건 이 배열 하나가 정한다.
 *
 * `song`(노래 따라부르기)만 **레퍼런스 음원이 있어야 성립한다** — 화면은 가이드 단계에서
 * 음원을 먼저 받고, 못 받으면 그 진단만 건너뛴다(진단 전체를 막지 않는다).
 */
export type TestId = 'sustain' | 'low' | 'high' | 'song';

/**
 * 측정 화면이 그리는 그림. **진단마다 재는 것이 달라서 그림도 달라야 한다** — 하나로
 * 통일하면 화면이 측정 대상과 다른 말을 한다.
 *
 * - `sustain` — 가로 타임라인. 기준음이 주어지고, 표식은 **그 기준음 대비** 위아래로
 *   움직인다. 기준음을 잡고 있으면 표식이 가운데에 머물고 트랙이 채워진다 — 즉 흔들림은
 *   못 맞추고 있다는 신호이지 잘하고 있다는 신호가 아니다(그래서 예전의 "세로 고정"
 *   결정을 뒤집었다: 고정이면 화면이 맞았는지 틀렸는지를 아예 말하지 않는다).
 * - `gauge` — 세로 게이지. 재는 건 **어디까지** 갔느냐(가장 낮은 음 / 가장 높은 음)라서
 *   위아래 한 축이 곧 측정값이고, 지금 음이름을 왼쪽에 같이 세운다.
 * - `trace` — 시간축 음정 곡선. 정답 멜로디를 따라갔느냐를 보므로 지나간 궤적이 화면에
 *   남아 있어야 한다.
 */
export type RecordViz = 'sustain' | 'gauge' | 'trace';

export interface RecordTest {
  id: TestId;
  /** 이 진단이 채우는 세그먼트들 — 한 화면 안에서 이 순서로 녹음한다. */
  segments: SegmentId[];
  /** 어떤 그림으로 보여줄지. 화면이 아니라 여기가 정한다(진단이 늘 때 화면을 안 뒤지게). */
  viz: RecordViz;
  /**
   * 진행 눈금(초). **다 차도 녹음을 안 끊는다** — 진행이 보이라고 두는 눈금이다.
   */
  gaugeSec?: number;
  /**
   * 제한시간(초). 지나면 자동으로 끊는다. 눈금과 정반대 의미라 화면도 **남은 시간**을
   * 보여준다: 저음 테스트에서 채워지는 게이지를 쓰면 "오래 버티기"를 잘한 게 되는데,
   * 재는 건 얼마나 낮은 음이냐지 얼마나 오래냐가 아니다.
   */
  limitSec?: number;
  titleKey: string;
  /** 안내 팝업의 한 줄 요약. */
  summaryKey: string;
  /** 가이드 카드 본문. */
  descKey: string;
}

const testKey = (id: TestId, field: string): string =>
  `web.diagnosis.tests.${id}.${field}`;

/**
 * 지속음 진단의 **녹음 상한**(초). 사양: 최대 30초.
 *
 * 눈금(`gaugeSec`)과 다른 값인 게 핵심이다. 눈금은 "여기까지 뻗으면 만점"이고 다 차도
 * 안 끊지만, 이 값은 기계가 끊는 자리다. 예전엔 상한이 아예 없어서 마이크가 열린 채로
 * 무한정 남을 수 있었다.
 */
export const SUSTAIN_LIMIT_SEC = 30;

/**
 * 지속음 진단이 **스스로 끝나는** 무음 길이(초). 사양: 흰 그래프(= 안 채워지는 구간)가
 * 5초 이상 이어지면 자동 종료.
 *
 * "무음"이 아니라 "안 채워짐"이다 — 소리를 내고 있어도 기준음을 못 맞추면 트랙은 안
 * 차고, 그 상태로 5초가 지나면 더 재도 얻을 게 없다. 판정은 화면이 아니라
 * `sustainIdleSec`(아래)이 하고, 상태기계가 그 값을 읽는다.
 */
export const SUSTAIN_IDLE_STOP_SEC = 5;

/** 저음·고음 진단의 제한시간(초) — 시안의 `소요시간 : 20초`. */
export const RANGE_LIMIT_SEC = 20;

export const RECORD_TESTS: RecordTest[] = [
  {
    id: 'sustain',
    viz: 'sustain',
    segments: ['sustain'],
    // 엔진이 호흡 지지력 만점으로 보는 길이(voice-engine GOAL)와 같은 눈금을 쓴다 —
    // 화면이 다 찼는데 점수가 60점이면 둘 중 하나는 거짓말이 된다.
    gaugeSec: GOAL,
    limitSec: SUSTAIN_LIMIT_SEC,
    titleKey: testKey('sustain', 'title'),
    summaryKey: testKey('sustain', 'summary'),
    descKey: testKey('sustain', 'desc'),
  },
  {
    id: 'low',
    viz: 'gauge',
    segments: ['low'],
    limitSec: RANGE_LIMIT_SEC,
    titleKey: testKey('low', 'title'),
    summaryKey: testKey('low', 'summary'),
    descKey: testKey('low', 'desc'),
  },
  {
    id: 'high',
    viz: 'gauge',
    segments: ['high'],
    limitSec: RANGE_LIMIT_SEC,
    titleKey: testKey('high', 'title'),
    summaryKey: testKey('high', 'summary'),
    descKey: testKey('high', 'desc'),
  },
  {
    id: 'song',
    viz: 'trace',
    segments: ['song'],
    // 레퍼런스가 준비되면 **멜로디 길이가 이 값을 대체한다**(`takeSpanSec`). 여기 남은
    // 20초는 레퍼런스를 못 받았을 때의 폴백이다 — 예전엔 이게 실제 제한시간이었고,
    // "음원이 13초"라는 (틀린) 전제 위에 있었다. 실제 음원은 6.9초다.
    limitSec: 20,
    titleKey: testKey('song', 'title'),
    summaryKey: testKey('song', 'summary'),
    descKey: testKey('song', 'desc'),
  },
];

/**
 * 이 테이크가 몇 초짜리인가. **녹음 제한시간이자 곡선 가로축의 폭**이다 — 세 소비처
 * (`useRecordMachine`의 자동 종료, `RecordTestView`의 남은 시간 표시와 축)가 이 함수
 * 하나를 읽는다.
 *
 * 둘이 따로 정하면 축이 안 덮는 구간이 생기고, `xOfTime`의 클램프가 그 구간의 점을 전부
 * 오른쪽 끝에 물린다 — 실측 6.41초 축에 20초 제한, 13.6초치가 x=100에 세로선으로
 * 쌓였다(`.claude/20260805/DEBUG-15-49-40.md`). 제한시간이 음원보다 짧으면 노래가
 * 중간에 끊기는 문제도 같이 걸려 있다.
 *
 * 노래는 **멜로디 길이**를 쓴다: 따라 부를 것이 끝나면 잴 것도 끝난다. `melody`는 레퍼런스가
 * 준비됐을 때만 들어오고, 노래가 아닌 진단에서도 호출자가 들고 있을 수 있어 `id`로 거른다 —
 * 안 거르면 저음 진단이 노래 길이만큼만 녹음된다.
 */
export const takeSpanSec = (
  test: RecordTest,
  melody?: MelodyNote[],
  /** 노래 음원의 길이(ms). 차트가 음원보다 짧아도 재생이 안 끊기게 하는 값이다. */
  audioDurationMs?: number,
): number | null =>
  (test.id === 'song' && melody
    ? melodySpanSec(melody, audioDurationMs)
    : null) ??
  test.limitSec ??
  null;

/**
 * 진단 전체 예상 소요(분) — 안내 팝업의 "약 n분". 제한시간 합(지속음 최대 30, 저음·고음
 * 20+20, 노래는 레퍼런스 길이라 ~7) + 안내·확인·재시도 여유. 진단이 늘면 여기도 같이
 * 움직여야 한다: 문구가 2분이라고 말하는 동안 화면이 3분을 쓰면 사용자는 중간에 멈춘다.
 */
export const ESTIMATED_MINUTES = 3;

export interface SegmentPrompt {
  /** 측정 화면 지시문. */
  titleKey: string;
  subtitleKey: string;
  /** 앞 세그먼트를 마치고 이어서 이걸 잴 때의 안내. 첫 세그먼트는 쓰이지 않는다. */
  upNextKey: string;
}

const segmentPrompt = (id: SegmentId): SegmentPrompt => ({
  titleKey: `web.diagnosis.segments.${id}.title`,
  subtitleKey: `web.diagnosis.segments.${id}.subtitle`,
  upNextKey: `web.diagnosis.segments.${id}.upNext`,
});

export const SEGMENT_PROMPTS: Record<SegmentId, SegmentPrompt> = {
  sustain: segmentPrompt('sustain'),
  low: segmentPrompt('low'),
  high: segmentPrompt('high'),
  song: segmentPrompt('song'),
};

/** 지금 몇 번째 진단의 몇 번째 세그먼트인가. */
export interface RecordPosition {
  testIndex: number;
  segIndex: number;
}

export const RECORD_START: RecordPosition = { testIndex: 0, segIndex: 0 };

export const testAt = (pos: RecordPosition): RecordTest =>
  RECORD_TESTS[pos.testIndex];

export const segmentAt = (pos: RecordPosition): SegmentId =>
  testAt(pos).segments[pos.segIndex];

/** 이 진단의 마지막 세그먼트인가 — 완료 다이얼로그를 띄울 자리. */
export const isLastSegment = (pos: RecordPosition): boolean =>
  pos.segIndex >= testAt(pos).segments.length - 1;

export const isLastTest = (pos: RecordPosition): boolean =>
  pos.testIndex >= RECORD_TESTS.length - 1;

/**
 * 다음 위치 — 세그먼트가 남았으면 같은 진단 안에서 전진하고, 없으면 다음 진단의 첫
 * 세그먼트로 넘어간다. 전부 끝났으면 null(= 리포트 생성).
 */
export const nextPosition = (pos: RecordPosition): RecordPosition | null => {
  if (!isLastSegment(pos)) {
    return { ...pos, segIndex: pos.segIndex + 1 };
  }
  return isLastTest(pos) ? null : { testIndex: pos.testIndex + 1, segIndex: 0 };
};

/**
 * 이 진단을 **통째로** 건너뛴 자리(다음 진단의 첫 세그먼트, 없으면 null = 분석).
 *
 * 세그먼트 단위 전진(`nextPosition`)과 다른 함수인 이유: 건너뛰기는 진단 하나가 성립하지
 * 않을 때(레퍼런스 음원을 못 받았다) 쓰는 문이라, 같은 진단의 남은 세그먼트로 넘어가면
 * 안 되는 소리를 또 요구하게 된다.
 */
export const nextTestPosition = (pos: RecordPosition): RecordPosition | null =>
  isLastTest(pos) ? null : { testIndex: pos.testIndex + 1, segIndex: 0 };

const NO_SOUND: CopyRef = { key: 'web.diagnosis.attempt.noSound', params: {} };

/**
 * 방금 시도가 뭘 남겼는지 한 줄. 재시도가 의미 있으려면 **다시 할지 판단할 근거**가
 * 있어야 한다 — "잘 됐나요?"만 묻고 값을 안 보여주면 사용자는 찍는 수밖에 없다.
 *
 * 판정 단위는 **세그먼트**다: 저음·고음은 한 진단이지만 재는 값이 서로 다르다.
 *
 * 문구가 아니라 **키**를 낸다 — 이 모듈은 React 밖이라 `useTranslation`이 없고, `i18n.t`를
 * 직접 부르면 순수 설정이 i18n 런타임에 묶인다(테스트도 같이 묶인다).
 */
export const describeAttempt = (
  // 노래는 **여기서 다루지 않는다** — 이 함수가 받는 건 clip 하나뿐인데 채점은 멜로디가
  // 있어야 성립한다(`songScoring`). 타입으로 막아 두면 없는 근거로 요약을 지어내는 경로가
  // 생기지 않는다.
  segment: Exclude<SegmentId, 'song'>,
  clip: Clip,
): CopyRef => {
  if (segment === 'sustain') {
    return clip.voicedSec > 0
      ? {
          key: 'web.diagnosis.attempt.sustain',
          params: { value: clip.voicedSec.toFixed(1) },
        }
      : NO_SOUND;
  }
  const hz = rangeHz(clip, segment);
  // 측정 화면이 음이름을 안 보여주게 된 뒤로, 이 문장이 **음역의 양끝을 말하는 유일한
  // 자리**다(측정 직후 확인 다이얼로그). 화면에서 지우면 저음·고음 진단은 자기가 무엇을
  // 쟀는지 어디서도 말하지 않는다.
  return hz == null
    ? NO_SOUND
    : {
        key: `web.diagnosis.bound.${segment}`,
        params: noteParams(freqToMidi(hz)),
      };
};

/**
 * 이 시도로 지표가 나오는지 — **세그먼트 단위 유효성 판정**(티켓 12).
 *
 * `/a/` 길게가 흔들리면(`isValidVoice`: 유효 프레임 10개 미만이거나, 검출기 오검출을
 * 걷어낸 프레임이 과반이 못 되거나, 그 프레임들의 cent-std ≥ 200)
 * `buildReport`가 통째로 null을 낸다. 그걸 마지막 테스트에서야 알면 세 개를 다 다시
 * 찍어야 한다 — 재시도를 테스트 단위로 만든 이유가 사라진다. 그래서 그 자리에서 말한다.
 *
 * 막지는 않는다: 여기서 전진을 잠그면 조건을 못 맞추는 사용자가 갇힌다. 최종 판정은
 * 여전히 엔진이 하고, 이건 경고다.
 */
export const attemptWarning = (
  segment: SegmentId,
  clip: Clip,
): CopyRef | null => {
  if (segment === 'sustain') {
    return isValidVoice(clip)
      ? null
      : { key: 'web.diagnosis.attempt.unstable', params: {} };
  }
  if (segment === 'song') {
    // 노래는 clip 하나로 판정할 수 없다 — 채점이 멜로디를 본다(`songScoring`).
    return null;
  }
  // **아무 소리도 안 잡힌 것도 "지표가 안 나온다"** 다. 예전에는 요약만 바뀌고 경고가
  // 없어서, 한 음도 못 잰 회차가 축하 화면으로 끝났다(2026-09-16 실기기: 2·3단계가
  // "소리가 잡히지 않았어요" 아래에서 마스코트와 함께 `다음`을 권했다).
  return rangeHz(clip, segment) == null
    ? { key: 'web.diagnosis.attempt.noRange', params: {} }
    : null;
};
